import React, { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { db } from '../firebase/config';
import {
    FaBook,
    FaMoneyBillWave,
    FaFileInvoice,
    FaUndo,
    FaChevronDown,
    FaChevronUp,
    FaLightbulb,
    FaCalculator,
    FaCheckCircle,
    FaExclamationTriangle,
    FaSpinner,
    FaDownload,
    FaCalendarAlt,
    FaFilter
} from 'react-icons/fa';

const TrainingPaymentGSTReturn = () => {
    const [expandedSection, setExpandedSection] = useState('gst-data');
    const [loading, setLoading] = useState(true);
    const [trainingsData, setTrainingsData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    // Filters
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [selectedMonth, setSelectedMonth] = useState('all');
    const [selectedQuarter, setSelectedQuarter] = useState('all');
    const [selectedGSTRate, setSelectedGSTRate] = useState('18'); // Default 18%

    // GST Summary
    const [gstSummary, setGstSummary] = useState({
        totalRevenue: 0,
        taxableValue: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        totalGST: 0,
        totalTransactions: 0
    });

    const toggleSection = (section) => {
        setExpandedSection(expandedSection === section ? null : section);
    };

    // Fetch training payment data from Firebase
    useEffect(() => {
        console.log('🔍 Fetching training data from Firebase...');
        console.log('📍 Path: HTAMS/company/trainings');

        const trainingRef = ref(db, 'HTAMS/company/trainings');

        const unsubscribe = onValue(trainingRef, (snapshot) => {
            const data = snapshot.val();
            console.log('📊 Raw Firebase data:', data);
            console.log('📊 Number of trainings found:', data ? Object.keys(data).length : 0);

            if (data) {
                const payments = [];
                let totalTrainings = 0;
                let totalParticipants = 0;

                Object.entries(data).forEach(([trainingId, trainingData]) => {
                    totalTrainings++;
                    console.log(`\n📚 Training ${totalTrainings} (ID: ${trainingId}):`, {
                        location: trainingData.location,
                        venue: trainingData.venue,
                        startDate: trainingData.startDate,
                        fees: trainingData.fees,
                        hasParticipants: !!trainingData.participants,
                        participantCount: trainingData.participants ? Object.keys(trainingData.participants).length : 0
                    });

                    if (trainingData.participants) {
                        Object.entries(trainingData.participants).forEach(([participantId, participant]) => {
                            totalParticipants++;

                            // Get payment status
                            const rawStatus = participant.paymentStatus ||
                                participant.paymentDetails?.payment_status ||
                                'pending';
                            const paymentStatus = (rawStatus === 'completed' || rawStatus === 'success') ? 'success' : rawStatus;

                            // Get amount paid
                            const amountPaid = participant.feePaid ||
                                participant.paymentDetails?.amount_paid ||
                                participant.amountPaid ||
                                0;

                            // Use expected amount (training fees) if no amount paid
                            const finalAmount = amountPaid > 0 ? amountPaid : (trainingData.fees || 0);

                            console.log(`  👤 Participant ${totalParticipants} (ID: ${participantId.slice(-8)}):`, {
                                name: participant.name,
                                firmName: participant.firmName,
                                status: paymentStatus,
                                amountPaid: amountPaid,
                                finalAmount: finalAmount,
                                trainingFees: trainingData.fees
                            });

                            // Include ALL participants
                            const registeredAt = participant.registeredAt ||
                                participant.joinedAt ||
                                participant.createdAt ||
                                Date.now();
                            const paymentDate = new Date(registeredAt);

                            // Calculate GST based on selected rate
                            const gstRatePercent = selectedGSTRate !== 'all' ? parseFloat(selectedGSTRate) : 18;
                            const gstRate = gstRatePercent / 100;
                            const taxableValue = finalAmount / (1 + gstRate);
                            const totalGST = finalAmount - taxableValue;

                            // Determine if intra-state or inter-state
                            const participantState = participant.state ||
                                participant.city ||
                                participant.address?.state ||
                                'Maharashtra';
                            const companyState = 'Maharashtra';
                            const isIntraState = participantState === companyState;

                            payments.push({
                                id: participantId,
                                trainingId,
                                trainingLocation: trainingData.location || trainingData.venue || 'N/A',
                                trainingStartDate: trainingData.startDate,
                                participantName: participant.name || 'N/A',
                                participantEmail: participant.email || 'N/A',
                                participantPhone: participant.phone || participant.mobile || 'N/A',
                                firmName: participant.firmName || 'N/A',
                                participantState: participantState,
                                amountPaid: finalAmount,
                                taxableValue: taxableValue,
                                cgst: isIntraState ? totalGST / 2 : 0,
                                sgst: isIntraState ? totalGST / 2 : 0,
                                igst: !isIntraState ? totalGST : 0,
                                totalGST: totalGST,
                                paymentDate: paymentDate,
                                year: paymentDate.getFullYear(),
                                month: paymentDate.getMonth() + 1,
                                quarter: Math.ceil((paymentDate.getMonth() + 1) / 3),
                                paymentId: participant.paymentDetails?.razorpay_payment_id ||
                                    participant.paymentDetails?.payment_link_id ||
                                    participant.paymentId ||
                                    'N/A',
                                isIntraState: isIntraState,
                                paymentStatus: paymentStatus,
                                mode: participant.paymentDetails?.payment_method || participant.mode || 'N/A'
                            });
                        });
                    } else {
                        console.log(`  ⚠️ No participants found for this training`);
                    }
                });

                console.log('\n✅ Data Summary:', {
                    totalTrainings,
                    totalParticipants,
                    paymentsProcessed: payments.length,
                    trainingIds: Object.keys(data)
                });

                console.log('💰 Sample Payment Records (first 3):', payments.slice(0, 3));

                setTrainingsData(payments);
                setFilteredData(payments);
                calculateGSTSummary(payments);
            } else {
                console.warn('⚠️ No training data found in Firebase at HTAMS/company/trainings');
            }
            setLoading(false);
        }, (error) => {
            console.error('❌ Firebase error:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Filter data based on selected filters and recalculate GST
    useEffect(() => {
        let filtered = [...trainingsData];

        if (selectedYear !== 'all') {
            filtered = filtered.filter(p => p.year.toString() === selectedYear);
        }

        if (selectedMonth !== 'all') {
            filtered = filtered.filter(p => p.month.toString() === selectedMonth);
        }

        if (selectedQuarter !== 'all') {
            filtered = filtered.filter(p => p.quarter.toString() === selectedQuarter);
        }

        // Recalculate GST based on selected rate
        if (selectedGSTRate !== 'all') {
            const gstRatePercent = parseFloat(selectedGSTRate);
            const gstRate = gstRatePercent / 100;

            filtered = filtered.map(payment => {
                const taxableValue = payment.amountPaid / (1 + gstRate);
                const totalGST = payment.amountPaid - taxableValue;

                return {
                    ...payment,
                    taxableValue: taxableValue,
                    cgst: payment.isIntraState ? totalGST / 2 : 0,
                    sgst: payment.isIntraState ? totalGST / 2 : 0,
                    igst: !payment.isIntraState ? totalGST : 0,
                    totalGST: totalGST
                };
            });
        }

        setFilteredData(filtered);
        calculateGSTSummary(filtered);
    }, [selectedYear, selectedMonth, selectedQuarter, selectedGSTRate, trainingsData]);

    const calculateGSTSummary = (data) => {
        const summary = data.reduce((acc, payment) => {
            return {
                totalRevenue: acc.totalRevenue + payment.amountPaid,
                taxableValue: acc.taxableValue + payment.taxableValue,
                cgst: acc.cgst + payment.cgst,
                sgst: acc.sgst + payment.sgst,
                igst: acc.igst + payment.igst,
                totalGST: acc.totalGST + payment.totalGST,
                totalTransactions: acc.totalTransactions + 1
            };
        }, {
            totalRevenue: 0,
            taxableValue: 0,
            cgst: 0,
            sgst: 0,
            igst: 0,
            totalGST: 0,
            totalTransactions: 0
        });

        setGstSummary(summary);
    };

    // Export to CSV
    const exportToCSV = () => {
        const headers = [
            'Date', 'Training', 'Participant', 'Firm Name', 'State', 'Type',
            'Total Amount', 'Taxable Value', 'CGST (9%)', 'SGST (9%)', 'IGST (18%)', 'Total GST', 'Payment ID'
        ];

        const rows = filteredData.map(p => [
            p.paymentDate.toLocaleDateString(),
            p.trainingLocation,
            p.participantName,
            p.firmName,
            p.participantState,
            p.isIntraState ? 'Intra-State' : 'Inter-State',
            p.amountPaid.toFixed(2),
            p.taxableValue.toFixed(2),
            p.cgst.toFixed(2),
            p.sgst.toFixed(2),
            p.igst.toFixed(2),
            p.totalGST.toFixed(2),
            p.paymentId
        ]);

        // Add summary
        rows.push([]);
        rows.push(['SUMMARY']);
        rows.push(['Total Transactions', gstSummary.totalTransactions]);
        rows.push(['Total Revenue', gstSummary.totalRevenue.toFixed(2)]);
        rows.push(['Taxable Value', gstSummary.taxableValue.toFixed(2)]);
        rows.push(['Total CGST', gstSummary.cgst.toFixed(2)]);
        rows.push(['Total SGST', gstSummary.sgst.toFixed(2)]);
        rows.push(['Total IGST', gstSummary.igst.toFixed(2)]);
        rows.push(['Total GST Collected', gstSummary.totalGST.toFixed(2)]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `training-gst-return-${selectedYear}-${selectedMonth !== 'all' ? selectedMonth : 'all'}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // Get available years from data
    const availableYears = [...new Set(trainingsData.map(p => p.year))].sort((a, b) => b - a);

    return (
        <div className="training-gst-container">
            {/* Compact Header */}
            <div className="compact-header">
                <div className="header-left">
                    <FaFileInvoice className="header-icon" />
                    <div>
                        <h1>Training GST Return</h1>
                        <p>{filteredData.length} transactions</p>
                    </div>
                </div>
                <button className="export-btn-compact" onClick={exportToCSV} disabled={loading}>
                    <FaDownload /> Export
                </button>
            </div>

            {loading ? (
                <div className="loading-compact">
                    <FaSpinner className="spinner" />
                    <p>Loading...</p>
                </div>
            ) : (
                <>
                    {/* Compact Filters */}
                    <div className="filters-compact">
                        <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="filter-select">
                            <option value="all">All Years</option>
                            {availableYears.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>

                        <select value={selectedQuarter} onChange={(e) => {
                            setSelectedQuarter(e.target.value);
                            setSelectedMonth('all');
                        }} className="filter-select">
                            <option value="all">All Quarters</option>
                            <option value="1">Q1</option>
                            <option value="2">Q2</option>
                            <option value="3">Q3</option>
                            <option value="4">Q4</option>
                        </select>

                        <select value={selectedMonth} onChange={(e) => {
                            setSelectedMonth(e.target.value);
                            setSelectedQuarter('all');
                        }} className="filter-select">
                            <option value="all">All Months</option>
                            <option value="1">Jan</option>
                            <option value="2">Feb</option>
                            <option value="3">Mar</option>
                            <option value="4">Apr</option>
                            <option value="5">May</option>
                            <option value="6">Jun</option>
                            <option value="7">Jul</option>
                            <option value="8">Aug</option>
                            <option value="9">Sep</option>
                            <option value="10">Oct</option>
                            <option value="11">Nov</option>
                            <option value="12">Dec</option>
                        </select>

                        <select value={selectedGSTRate} onChange={(e) => setSelectedGSTRate(e.target.value)} className="filter-select">
                            <option value="all">All Rates</option>
                            <option value="0">0%</option>
                            <option value="3">3%</option>
                            <option value="5">5%</option>
                            <option value="12">12%</option>
                            <option value="18">18%</option>
                            <option value="28">28%</option>
                        </select>
                    </div>

                    {/* Compact Summary Cards */}
                    <div className="summary-compact">
                        <div className="sum-card blue">
                            <div className="sum-label">Revenue</div>
                            <div className="sum-value">₹{(gstSummary.totalRevenue / 1000).toFixed(1)}k</div>
                        </div>
                        <div className="sum-card green">
                            <div className="sum-label">Taxable</div>
                            <div className="sum-value">₹{(gstSummary.taxableValue / 1000).toFixed(1)}k</div>
                        </div>
                        <div className="sum-card orange">
                            <div className="sum-label">CGST</div>
                            <div className="sum-value">₹{(gstSummary.cgst / 1000).toFixed(1)}k</div>
                        </div>
                        <div className="sum-card purple">
                            <div className="sum-label">SGST</div>
                            <div className="sum-value">₹{(gstSummary.sgst / 1000).toFixed(1)}k</div>
                        </div>
                        <div className="sum-card pink">
                            <div className="sum-label">IGST</div>
                            <div className="sum-value">₹{(gstSummary.igst / 1000).toFixed(1)}k</div>
                        </div>
                        <div className="sum-card red">
                            <div className="sum-label">Total GST</div>
                            <div className="sum-value">₹{(gstSummary.totalGST / 1000).toFixed(1)}k</div>
                        </div>
                    </div>

                    {/* Compact Table */}
                    <div className="table-compact">
                        <div className="table-header-compact">
                            <h3>GST Transactions</h3>
                            <span className="badge-compact">{filteredData.length}</span>
                        </div>

                        {filteredData.length === 0 ? (
                            <div className="no-data-compact">
                                <FaExclamationTriangle />
                                <p>No data for selected filters</p>
                            </div>
                        ) : (
                            <div className="table-scroll-compact">
                                <table className="gst-table-compact">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Date</th>
                                            <th>Training</th>
                                            <th>Participant</th>
                                            <th>State</th>
                                            <th>Type</th>
                                            <th className="text-right">Total</th>
                                            <th className="text-right">Taxable</th>
                                            <th className="text-right">CGST</th>
                                            <th className="text-right">SGST</th>
                                            <th className="text-right">IGST</th>
                                            <th className="text-right">GST</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.map((payment, index) => (
                                            <tr key={payment.id}>
                                                <td className="row-number">{index + 1}</td>
                                                <td className="date-cell">{payment.paymentDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}</td>
                                                <td>
                                                    <div className="cell-main">{payment.trainingLocation}</div>
                                                </td>
                                                <td>
                                                    <div className="cell-main">{payment.participantName}</div>
                                                    <div className="cell-sub">{payment.firmName}</div>
                                                </td>
                                                <td>{payment.participantState}</td>
                                                <td>
                                                    <span className={`type-badge-table ${payment.isIntraState ? 'intra' : 'inter'}`}>
                                                        {payment.isIntraState ? 'Intra' : 'Inter'}
                                                    </span>
                                                </td>
                                                <td className="amount-cell">₹{payment.amountPaid.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                                                <td className="amount-cell">₹{payment.taxableValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                                                <td className="amount-cell gst-cell">₹{payment.cgst.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                                                <td className="amount-cell gst-cell">₹{payment.sgst.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                                                <td className="amount-cell gst-cell">₹{payment.igst.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                                                <td className="amount-cell total-cell">₹{payment.totalGST.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="total-row">
                                            <td colSpan="6"><strong>TOTAL</strong></td>
                                            <td className="amount-cell"><strong>₹{gstSummary.totalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></td>
                                            <td className="amount-cell"><strong>₹{gstSummary.taxableValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></td>
                                            <td className="amount-cell gst-cell"><strong>₹{gstSummary.cgst.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></td>
                                            <td className="amount-cell gst-cell"><strong>₹{gstSummary.sgst.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></td>
                                            <td className="amount-cell gst-cell"><strong>₹{gstSummary.igst.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></td>
                                            <td className="amount-cell total-cell"><strong>₹{gstSummary.totalGST.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Compact Info Card */}
                    <div className="info-compact">
                        <div className="info-icon-compact"><FaLightbulb /></div>
                        <div className="info-text-compact">
                            <strong>GST Filing Deadlines</strong>
                            <p>GSTR-1: 11th | GSTR-3B: 20th | Rate: 18% | SAC: 999293</p>
                        </div>
                    </div>
                </>
            )}

            <style>{`
                .training-gst-container {
                    padding: 12px;
                    max-width: 100%;
                    background: #f5f7fa;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                }

                /* Compact Header */
                .compact-header {
                    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
                    border-radius: 12px;
                    padding: 16px;
                    margin-bottom: 12px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    box-shadow: 0 2px 8px rgba(30, 64, 175, 0.2);
                }

                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    color: white;
                }

                .header-icon {
                    font-size: 1.8rem;
                    color: #60a5fa;
                }

                .compact-header h1 {
                    margin: 0;
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: white;
                }

                .compact-header p {
                    margin: 2px 0 0 0;
                    font-size: 0.75rem;
                    color: #bfdbfe;
                }

                .export-btn-compact {
                    background: rgba(255, 255, 255, 0.2);
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    color: white;
                    padding: 8px 16px;
                    border-radius: 8px;
                    font-size: 0.85rem;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-weight: 600;
                }

                .export-btn-compact:hover {
                    background: rgba(255, 255, 255, 0.3);
                }

                .export-btn-compact:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                /* Loading */
                .loading-compact {
                    text-align: center;
                    padding: 40px 20px;
                    background: white;
                    border-radius: 12px;
                }

                .spinner {
                    font-size: 2rem;
                    color: #3b82f6;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                /* Compact Filters */
                .filters-compact {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 8px;
                    margin-bottom: 12px;
                }

                .filter-select {
                    padding: 8px 10px;
                    border: 1px solid #cbd5e1;
                    border-radius: 8px;
                    font-size: 0.85rem;
                    background: white;
                    color: #334155;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .filter-select:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                /* Compact Summary Cards */
                .summary-compact {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 8px;
                    margin-bottom: 12px;
                }

                .sum-card {
                    background: white;
                    border-radius: 10px;
                    padding: 12px;
                    text-align: center;
                    border-left: 3px solid;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                }

                .sum-card.blue { border-left-color: #3b82f6; }
                .sum-card.green { border-left-color: #10b981; }
                .sum-card.orange { border-left-color: #f59e0b; }
                .sum-card.purple { border-left-color: #8b5cf6; }
                .sum-card.pink { border-left-color: #ec4899; }
                .sum-card.red { border-left-color: #ef4444; }

                .sum-label {
                    font-size: 0.7rem;
                    color: #64748b;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 4px;
                }

                .sum-value {
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: #1e293b;
                }

                /* Compact Table */
                .table-compact {
                    background: white;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                    margin-bottom: 12px;
                }

                .table-header-compact {
                    padding: 12px 16px;
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    border-bottom: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .table-header-compact h3 {
                    margin: 0;
                    font-size: 0.95rem;
                    color: #1e293b;
                    font-weight: 700;
                }

                .badge-compact {
                    background: #3b82f6;
                    color: white;
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                .no-data-compact {
                    text-align: center;
                    padding: 40px 20px;
                    color: #94a3b8;
                }

                .no-data-compact svg {
                    font-size: 2rem;
                    margin-bottom: 8px;
                }

                .table-scroll-compact {
                    overflow-x: auto;
                }

                /* GST Table */
                .gst-table-compact {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.8rem;
                }

                .gst-table-compact thead {
                    background: linear-gradient(135deg, #1e293b, #334155);
                    color: white;
                }

                .gst-table-compact th {
                    padding: 10px 8px;
                    text-align: left;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    white-space: nowrap;
                }

                .gst-table-compact th.text-right {
                    text-align: right;
                }

                .gst-table-compact td {
                    padding: 10px 8px;
                    border-bottom: 1px solid #f1f5f9;
                }

                .gst-table-compact tbody tr:hover {
                    background: #f8fafc;
                }

                .row-number {
                    background: #e0e7ff;
                    color: #3730a3;
                    font-weight: 700;
                    text-align: center;
                    border-radius: 4px;
                }

                .date-cell {
                    font-size: 0.75rem;
                    color: #64748b;
                    font-weight: 600;
                    white-space: nowrap;
                }

                .cell-main {
                    font-weight: 600;
                    color: #1e293b;
                    font-size: 0.85rem;
                }

                .cell-sub {
                    font-size: 0.7rem;
                    color: #64748b;
                    margin-top: 2px;
                }

                .type-badge-table {
                    display: inline-block;
                    padding: 3px 8px;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-weight: 600;
                }

                .type-badge-table.intra {
                    background: #dbeafe;
                    color: #1e40af;
                }

                .type-badge-table.inter {
                    background: #fef3c7;
                    color: #92400e;
                }

                .amount-cell {
                    text-align: right;
                    font-weight: 600;
                    color: #334155;
                    white-space: nowrap;
                }

                .gst-cell {
                    color: #f59e0b;
                }

                .total-cell {
                    color: #ef4444;
                    font-weight: 700;
                }

                .total-row {
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    font-weight: 700;
                }

                .total-row td {
                    padding: 14px 8px;
                    border-top: 2px solid #cbd5e1;
                }

                /* Compact Info */
                .info-compact {
                    background: linear-gradient(135deg, #eff6ff, #dbeafe);
                    border-left: 3px solid #3b82f6;
                    border-radius: 10px;
                    padding: 12px;
                    display: flex;
                    gap: 12px;
                    align-items: flex-start;
                }

                .info-icon-compact {
                    font-size: 1.5rem;
                    color: #3b82f6;
                    flex-shrink: 0;
                }

                .info-text-compact {
                    flex: 1;
                }

                .info-text-compact strong {
                    display: block;
                    font-size: 0.85rem;
                    color: #1e293b;
                    margin-bottom: 4px;
                }

                .info-text-compact p {
                    margin: 0;
                    font-size: 0.75rem;
                    color: #475569;
                    line-height: 1.4;
                }

                /* Mobile Responsive */
                @media (max-width: 640px) {
                    .training-gst-container {
                        padding: 8px;
                    }

                    .summary-compact {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    .filters-compact {
                        grid-template-columns: 1fr;
                    }

                    .gst-table-compact {
                        font-size: 0.7rem;
                    }

                    .gst-table-compact th,
                    .gst-table-compact td {
                        padding: 6px 4px;
                    }
                }

                @media (min-width: 768px) {
                    .training-gst-container {
                        padding: 20px;
                        max-width: 100%;
                        margin: 0 auto;
                    }

                    .filters-compact {
                        grid-template-columns: repeat(4, 1fr);
                    }

                    .summary-compact {
                        grid-template-columns: repeat(6, 1fr);
                    }

                    .gst-table-compact {
                        font-size: 0.85rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default TrainingPaymentGSTReturn;
